import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lightbulb, Play } from "lucide-react";
import TopNav from "@/components/TopNav";

const TIMER_SECONDS = 5 * 60;
const NEXT_PATH = "/select-volunteer";

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function Discuss() {
  const navigate = useNavigate();
  const [state, setState] = useState("idle"); // idle | running | done
  const [remaining, setRemaining] = useState(TIMER_SECONDS);
  const intervalRef = useRef(null);

  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    },
    []
  );

  const start = () => {
    setState("running");
    setRemaining(TIMER_SECONDS);
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setState("done");
          navigate(NEXT_PATH);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="discuss-page"
    >
      <TopNav />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-10 pb-32 flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full text-center"
        >
          <span
            className="arcade-btn arcade-btn-ghost text-[10px] px-3 py-1.5"
            data-testid="discuss-badge"
          >
            Ideate
          </span>

          <h1
            className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight"
            data-testid="discuss-title"
          >
            Discuss your{" "}
            <span className="relative inline-block">
              <span className="relative z-10">game idea</span>
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 right-0 h-2.5 bg-[var(--accent)]/70 -rotate-1 rounded-sm z-0"
              />
            </span>{" "}
            together
          </h1>

          <p
            className="mt-4 text-base sm:text-lg text-[var(--text-main)]/80 max-w-xl mx-auto leading-relaxed"
            data-testid="discuss-subtitle"
          >
            Take five minutes as a team to talk through what game you want to
            build. Press <span className="font-semibold">Start</span> when you
            are ready.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="mt-8 w-full max-w-sm card-brut bg-white overflow-hidden"
          data-testid="discuss-timer-card"
        >
          <div className="flex items-center gap-2.5 px-4 py-3 border-b-[3px] border-[var(--border-heavy)] bg-[var(--primary)]">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--border-heavy)] bg-white shadow-[2px_2px_0px_var(--border-heavy)]">
              <Lightbulb strokeWidth={2.5} className="h-4 w-4 text-[var(--text-main)]" />
            </span>
            <p className="font-heading text-xs font-bold uppercase tracking-wider text-[var(--text-main)]">
              Idea Timer
            </p>
          </div>

          <div className="flex flex-col items-center gap-1.5 py-6 px-4">
            <span
              className="font-heading text-5xl sm:text-6xl font-bold tabular-nums text-[var(--text-main)]"
              data-testid="discuss-timer-display"
            >
              {formatTime(remaining)}
            </span>
            <span
              className="font-heading text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]"
              data-testid="discuss-timer-status"
            >
              {state === "idle" && "Ready when you are"}
              {state === "done" && "Time's up — nice discussion!"}
            </span>
          </div>
        </motion.div>
      </main>

      {/* Bottom nav — back + primary action */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
        data-testid="discuss-bottom-nav"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Back to home"
          data-testid="discuss-prev-btn"
          className="arcade-btn arcade-btn-icon"
        >
          <ChevronLeft strokeWidth={3} className="h-6 w-6" />
        </button>

        {state === "idle" && (
          <button
            type="button"
            onClick={start}
            data-testid="discuss-start-btn"
            className="arcade-btn"
          >
            <Play strokeWidth={3} className="h-5 w-5" fill="currentColor" />
            Start
          </button>
        )}

        {state === "running" && (
          <button
            type="button"
            onClick={() => navigate(NEXT_PATH)}
            aria-label="Skip timer and continue"
            data-testid="discuss-next-btn"
            className="arcade-btn arcade-btn-icon"
          >
            <ChevronRight strokeWidth={3} className="h-6 w-6" />
          </button>
        )}

        {state === "done" && (
          <button
            type="button"
            onClick={() => navigate(NEXT_PATH)}
            aria-label="Continue to role selection"
            data-testid="discuss-next-btn"
            className="arcade-btn arcade-btn-icon"
          >
            <ChevronRight strokeWidth={3} className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}
